import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";

import { CreditBreakdown, formatCredits } from "./creditBreakdown";
import { CreditTopUp } from "./CreditTopUp";
import { CreditBalance } from "./useCreditBalance";

interface CreditCostBlockProps {
	breakdown: CreditBreakdown;
	balance: CreditBalance;
	/** How the action charges, e.g. "You are charged only for the emails we find." */
	chargeNote?: string;
}

/** The total: a promise when the cost is known, the known part plus the run's when it is not. */
const totalText = (
	breakdown: CreditBreakdown,
	amount: (key: string, value: number) => string,
	variableTotal: string
): string => {
	if (!breakdown.isVariable) {
		return amount(breakdown.isUpTo ? "upToCredits" : "credits", breakdown.total);
	}

	return breakdown.total > 0 ? amount("knownPlusVariable", breakdown.total) : variableTotal;
};

/** "at least" after a maximum, "at most" after a cost the run decides, exact otherwise. */
const balanceAfterKey = (breakdown: CreditBreakdown): string => {
	if (breakdown.isVariable) {
		return "atMostCredits";
	}

	return breakdown.isUpTo ? "atLeastCredits" : "credits";
};

/**
 * The cost of an action, as the credit-confirm modal and the custom-column forms show it
 * (PREC-4263): a row per part when the selection splits, the total ("up to" when the charge
 * depends on results), the balance after ("at least" then), and what to do when it does not
 * cover the total. A part the run decides is named with its unit price and never summed into a
 * promised total; what the API needs to start is its own line (review S1, M2). Renders
 * `buildCreditBreakdown`'s answer and computes nothing itself.
 */
export const CreditCostBlock: FunctionComponent<CreditCostBlockProps> = ({
	breakdown,
	balance,
	chargeNote,
}) => {
	const { t } = useTranslation();
	const amount = (key: string, value: number) =>
		t(`creditConfirm.${key}`, { count: value, credits: formatCredits(value) });
	const { balance: state } = balance;

	return (
		<div className="flex flex-col gap-2 text-sm" data-test="credit-cost-block">
			{breakdown.showRows && (
				<ul className="flex flex-col gap-1 border-b border-border-light pb-2">
					{breakdown.rows.map((row) => (
						<li key={row.id} className="flex justify-between gap-4" data-test="credit-cost-row">
							<span className="text-text-meta">{row.label}</span>
							<span className="text-text whitespace-nowrap">
								{row.variable ? t("creditConfirm.variable") : amount("credits", row.credits)}
							</span>
						</li>
					))}
				</ul>
			)}
			<div className="flex justify-between gap-4 font-medium">
				<span className="text-text">{t("creditConfirm.total")}</span>
				<span
					className={classNames(breakdown.isInsufficient ? "text-status-error" : "text-text")}
					data-test="credit-cost-total"
				>
					{totalText(breakdown, amount, t("creditConfirm.variableTotal"))}
				</span>
			</div>
			{breakdown.requiredToStart > breakdown.total && (
				<div className="flex justify-between gap-4">
					<span className="text-text-meta">{t("creditConfirm.requiredToStart")}</span>
					<span
						className={classNames(breakdown.isInsufficient ? "text-status-error" : "text-text")}
						data-test="credit-required-to-start"
					>
						{amount("credits", breakdown.requiredToStart)}
					</span>
				</div>
			)}
			<div className="flex justify-between gap-4">
				<span className="text-text-meta">{t("creditConfirm.balanceAfter")}</span>
				{state.status === "loading" && (
					<span
						className="h-4 w-24 animate-pulse rounded bg-border-light"
						data-test="credit-balance-loading"
					/>
				)}
				{state.status === "unavailable" && (
					<span className="text-text-meta" data-test="credit-balance-after">
						{t("creditConfirm.balanceUnavailable")}
					</span>
				)}
				{breakdown.balanceAfter !== null && (
					<span
						className={classNames(breakdown.isInsufficient ? "text-status-error" : "text-text")}
						data-test="credit-balance-after"
					>
						{amount(balanceAfterKey(breakdown), breakdown.balanceAfter)}
					</span>
				)}
			</div>
			{state.status === "unavailable" && (
				<p className="text-xs text-text-meta">{t("creditConfirm.balanceUnavailableHint")}</p>
			)}
			{chargeNote && <p className="text-xs text-text-meta">{chargeNote}</p>}
			{breakdown.isInsufficient && state.status === "loaded" && (
				<p className="text-xs text-status-error" data-test="credit-insufficient">
					{t("creditConfirm.insufficient", {
						balance: formatCredits(state.credits),
						missing: formatCredits(breakdown.shortfall),
					})}{" "}
					<CreditTopUp hasSubscription={balance.hasSubscription} onClosed={balance.refetch} />
				</p>
			)}
		</div>
	);
};
